import { closeSnackbar, CustomContentProps, SnackbarContent, useSnackbar } from "notistack";
import { Box, Button, Card, CardContent, IconButton, Paper } from "@mui/material";
import { CheckCircle, CloseOutlined, Error, Info, WifiOff } from "@mui/icons-material";
import { P } from "theme/typography";
import { Amber, error, grey, success } from "theme/palette";
import theme from "../theme";
import { forwardRef, useCallback, useContext } from "react";
import { pluralize } from "helpers/coll";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import useInterval from "helpers/hooks/useInterval";

export const FormChageSnackbar = forwardRef<HTMLDivElement, CustomContentProps & { callback: () => Promise<void> }>(
  (props, ref) => {
    const { id, message, callback } = props;

    const { closeSnackbar } = useSnackbar();
    const handleDismiss = useCallback(() => {
      callback().finally(() => closeSnackbar(id));
    }, [id, closeSnackbar]);

    return (
      <SnackbarContent ref={ref}>
        <Card
          sx={{
            minWidth: 280,
            height: 58,
            display: "flex",
            backgroundImage: "linear-gradient(to right, #eef3fe 0%, #d6e0fc 30%, #baccfb 50%, #9eb7f9 100%)",
            backgroundRepeat: "no-repeat",
            animation: "shimmer 2.5s infinite",
            border: `1px solid ${theme.palette.primary.light}`,
            backgroundSize: "1000px 100%",
          }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
            }}>
            <Box display="flex" flexDirection="row" sx={{ alignItems: "center" }}>
              <Info sx={{ mr: 1, mt: "2px" }} color="primary" />
              <P sx={{ color: grey["700"] }} variant="body2">
                {message}
              </P>
              <Button variant="contained" size="small" sx={{ ml: 2 }} color="primary" onClick={handleDismiss}>
                Update
              </Button>
            </Box>
          </Box>
        </Card>
      </SnackbarContent>
    );
  }
);
FormChageSnackbar.displayName = "LensUpdateSnackbar";

export const LensEditorSendAlertSnackbar = forwardRef<
  HTMLDivElement,
  CustomContentProps & {
    onCancel: () => Promise<any>;
    onSend: () => Promise<any>;
  }
>((props, ref) => {
  const { id, onCancel, onSend } = props;
  const { timer, setTimer } = useContext(LensUpdateSnackbarContext);

  const handleDismiss = () => {
    closeSnackbar(id);
    setTimer && setTimer(undefined);
  };
  const handleCancel = () => {
    onCancel().finally(handleDismiss);
  };
  const handleSend = () => {
    onSend().finally(handleDismiss);
  };

  useInterval(() => {
    if (setTimer) {
      setTimer((prevSeconds) => {
        if (prevSeconds !== undefined) {
          if (prevSeconds <= 1) {
            handleDismiss();
          } else {
            return prevSeconds - 1;
          }
        }
      });
    }
  }, 1000);

  const minutes = timer !== undefined ? Math.round(timer / 60) : 0;
  const secondsRemainder = timer !== undefined ? timer % 60 : 0;
  const message = minutes
    ? `Update will be sent in ${minutes} ${pluralize(minutes, "minute", "minutes")}`
    : `Update will be sent in ${secondsRemainder} ${pluralize(secondsRemainder, "second", "seconds")}`;
  return (
    <SnackbarContent ref={ref}>
      <Box>
        <Card sx={{ minWidth: 280, height: 85, display: "flex" }}>
          <Paper sx={{ backgroundColor: theme.palette.primary.main, width: "4px" }}></Paper>
          <CardContent sx={{ display: "flex", flexDirection: "column" }}>
            <Box display="flex" flexDirection="row">
              <Info sx={{ mr: 1, mt: "2px" }} color="primary" />
              <P sx={{ color: grey["700"] }} variant="body2">
                <b>{message}</b>
              </P>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
              <Button onClick={handleCancel} color="secondary">
                Cancel Alert
              </Button>
              <Button onClick={handleSend}>Send Now</Button>
            </Box>
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
        </Card>
      </Box>
    </SnackbarContent>
  );
});
LensEditorSendAlertSnackbar.displayName = "LensEditorSendAlertSnackbar";

export const RefreshSnackbar = forwardRef<HTMLDivElement, CustomContentProps>((_props, ref) => {
  return (
    <SnackbarContent ref={ref}>
      <Card
        sx={{
          minWidth: 280,
          height: 58,
          display: "flex",
          backgroundImage: "linear-gradient(to right, #eef3fe 0%, #d6e0fc 30%, #baccfb 50%, #9eb7f9 100%)",
          backgroundRepeat: "no-repeat",
          animation: "shimmer 2.5s infinite",
          border: `1px solid ${theme.palette.primary.light}`,
          backgroundSize: "1000px 100%",
        }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
          }}>
          <Box display="flex" flexDirection="row" sx={{ alignItems: "center" }}>
            <Info sx={{ mr: 1, mt: "2px" }} color="primary" />
            <P sx={{ color: grey["700"] }} variant="body2">
              There is a new version of Pattern Data available.
            </P>
            <Button
              variant="contained"
              size="small"
              sx={{ ml: 2 }}
              color="primary"
              onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </Box>
        </Box>
      </Card>
    </SnackbarContent>
  );
});
RefreshSnackbar.displayName = "RefreshSnackbar";

export const InfoSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(({ id, message }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref}>
      <Box>
        <Card sx={{ minWidth: 280, height: 58, display: "flex" }}>
          <Paper sx={{ backgroundColor: theme.palette.primary.main, width: "4px" }}></Paper>
          <CardContent sx={{ display: "flex" }}>
            <Box display="flex" flexDirection="row">
              <Info sx={{ mr: 1, mt: "2px" }} color="primary" />
              <P sx={{ color: grey["700"] }} variant="body2">
                <b>{message}</b>
              </P>
            </Box>
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <IconButton onClick={handleDismiss}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </Card>
      </Box>
    </SnackbarContent>
  );
});
InfoSnackbar.displayName = "InfoSnackbar";

export const SuccessSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(({ id, message }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref}>
      <Box>
        <Card sx={{ minWidth: 280, height: 58, display: "flex" }}>
          <Paper
            sx={{
              backgroundColor: success.light,
              width: "4px",
            }}></Paper>
          <CardContent sx={{ display: "flex" }}>
            <Box display="flex" flexDirection="row">
              <CheckCircle sx={{ mr: 1, mt: "2px" }} color="success" />
              <P sx={{ color: grey["700"] }} variant="body2">
                <b>{message}</b>
              </P>
            </Box>
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <IconButton onClick={handleDismiss}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </Card>
      </Box>
    </SnackbarContent>
  );
});
SuccessSnackbar.displayName = "SuccessSnackbar";

export const MultilineErrorSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(({ id, message }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref}>
      <Box>
        <Card sx={{ minWidth: 280, display: "flex" }}>
          <Paper
            sx={{
              backgroundColor: error.light,
              width: "4px",
            }}></Paper>
          <CardContent sx={{ display: "flex" }}>
            <Box display="flex" flexDirection="row">
              <Error sx={{ mr: 1, mt: "2px" }} color="error" />
              <P sx={{ color: grey["700"], whiteSpace: "pre-wrap" }} variant="body2">
                <b>{message}</b>
              </P>
            </Box>
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <IconButton onClick={handleDismiss}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </Card>
      </Box>
    </SnackbarContent>
  );
});
MultilineErrorSnackbar.displayName = "MultilineErrorSnackbar";

export const ErrorSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(({ id, message }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref}>
      <Box>
        <Card sx={{ minWidth: 280, height: 58, display: "flex" }}>
          <Paper
            sx={{
              backgroundColor: error.light,
              width: "4px",
            }}></Paper>
          <CardContent sx={{ display: "flex" }}>
            <Box display="flex" flexDirection="row">
              <Error sx={{ mr: 1, mt: "2px" }} color="error" />
              <P sx={{ color: grey["700"] }} variant="body2">
                <b>{message}</b>
              </P>
            </Box>
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <IconButton onClick={handleDismiss}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </Card>
      </Box>
    </SnackbarContent>
  );
});
ErrorSnackbar.displayName = "ErrorSnackbar";

export const ServerConnectionSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(({ id, message }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref}>
      <Box>
        <Card sx={{ minWidth: 280, height: 58, display: "flex" }}>
          <Paper
            sx={{
              backgroundColor: Amber["500"],
              width: "4px",
            }}></Paper>
          <CardContent sx={{ display: "flex" }}>
            <Box display="flex" flexDirection="row">
              <WifiOff sx={{ mr: 1, mt: "2px" }} />
              <P sx={{ color: grey["700"] }} variant="body2">
                <b>{message}</b>
              </P>
            </Box>
          </CardContent>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <IconButton onClick={handleDismiss}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </Card>
      </Box>
    </SnackbarContent>
  );
});
ServerConnectionSnackbar.displayName = "ServerConnectionSnackbar";
